import type { GridColumnVisibilityModel } from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../../contexts/AuthProvider";
import { localStorageService } from "../../../../services/LocalStorageService";

interface UseColumnVisibilityArgs {
	enabled: boolean;
	localStorageKey: string | undefined;
	/** Seeds the initial model when nothing is persisted, and is the target of `resetVisibility`. */
	defaultVisibility: GridColumnVisibilityModel | undefined;
	/** Notified with the resolved model on mount, on async-userId restore, and on every change. */
	onModelChange: ((model: GridColumnVisibilityModel) => void) | undefined;
}

interface UseColumnVisibilityResult {
	columnVisibilityModel: GridColumnVisibilityModel;
	/** Apply a user toggle: update state, persist (when keyed), and notify `onModelChange`. */
	setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
	/** Restore `defaultVisibility` (or show-all `{}` when unset), persist, and notify. */
	resetVisibility: () => void;
	/** The model `resetVisibility` applies — lets the grid pre-compute what a reset will reveal. */
	resetVisibilityModel: GridColumnVisibilityModel;
}

/**
 * Owns optional, persisted column-visibility for SystemDataGrid. The model is MUI's
 * `GridColumnVisibilityModel` (`{ field: false }` for hidden columns), stored per-user under the table's
 * `localStorageKey` + a `-columnVisibility` suffix. Structural mirror of `useColumnOrder` (including the
 * async-userId restore, since `useAuth` populates `userInfo` after mount). A stored model is merged OVER
 * `defaultVisibility` (`{ ...defaultVisibility, ...stored }`) so a column ADDED after the user's model was
 * saved inherits its default instead of reading as visible — otherwise every existing user would see each
 * new hidden-by-default column. Dynamic column sets (e.g. the nurse panels) are unaffected: their columns
 * aren't listed in `defaultVisibility`, so a field missing from both still reads as visible.
 *
 * When `enabled` is false the hook is inert (nothing is persisted; the grid ignores the model).
 */
export function useColumnVisibility({ enabled, localStorageKey, defaultVisibility, onModelChange }: UseColumnVisibilityArgs): UseColumnVisibilityResult {
	const { userInfo } = useAuth();
	const userId = userInfo?.id ?? "";

	const [columnVisibilityModel, setModelState] = useState<GridColumnVisibilityModel>(() => {
		const stored = enabled && localStorageKey && userId ? localStorageService.getTableColumnVisibility(localStorageKey, userId) : null;
		return stored ? { ...(defaultVisibility ?? {}), ...stored } : (defaultVisibility ?? {});
	});

	// `onModelChange` may be an inline closure — hold it in a ref so the notify effect keys only on the
	// model (not the callback identity), firing exactly once per resolved model (mount, restore, change).
	const onModelChangeRef = useRef(onModelChange);
	onModelChangeRef.current = onModelChange;
	useEffect(() => {
		onModelChangeRef.current?.(columnVisibilityModel);
	}, [columnVisibilityModel]);

	// `useAuth` populates userInfo asynchronously; if userId was empty at mount, retry the restore once it
	// arrives (mirrors useColumnOrder / useMultiSort). No-op if nothing was saved.
	const initialUserIdHadValueRef = useRef(!!userId);
	const defaultVisibilityRef = useRef(defaultVisibility);
	defaultVisibilityRef.current = defaultVisibility;
	useEffect(() => {
		if (initialUserIdHadValueRef.current || !userId || !enabled || !localStorageKey) return;
		initialUserIdHadValueRef.current = true;
		const stored = localStorageService.getTableColumnVisibility(localStorageKey, userId);
		if (stored) setModelState({ ...(defaultVisibilityRef.current ?? {}), ...stored });
	}, [userId, enabled, localStorageKey]);

	const setColumnVisibilityModel = useCallback(
		(model: GridColumnVisibilityModel) => {
			setModelState(model);
			if (enabled && localStorageKey && userId) localStorageService.saveTableColumnVisibility(localStorageKey, model, userId);
		},
		[enabled, localStorageKey, userId],
	);

	const resetVisibilityModel = useMemo(() => defaultVisibility ?? {}, [defaultVisibility]);
	const resetVisibility = useCallback(() => {
		setModelState(resetVisibilityModel);
		if (enabled && localStorageKey && userId) localStorageService.saveTableColumnVisibility(localStorageKey, resetVisibilityModel, userId);
	}, [enabled, localStorageKey, userId, resetVisibilityModel]);

	return { columnVisibilityModel, setColumnVisibilityModel, resetVisibility, resetVisibilityModel };
}
